import type { ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { statusOf } from '@/lib/leads';

/** A lead status as words on a chip; colour supports the word, never replaces it. */
export function StatusChip({ status }: { status: string }) {
  const s = statusOf(status);
  return <span className={`chip ${s.chip}`}>{s.label}</span>;
}

/** What an empty place is for, and how it fills up. Never just "nothing here". */
export function Empty({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-edge-strong px-5 py-10 text-center sm:px-10">
      <p className="font-semibold">{title}</p>
      <div className="mx-auto mt-2 max-w-md text-sm text-fg-dim">{children}</div>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  );
}

export function LoadError({ error, retry }: { error: unknown; retry?: () => void }) {
  const text = error instanceof Error ? error.message : String(error);
  return (
    <div role="alert" className="flex items-start gap-3 rounded-lg border border-alert/30 bg-alert-tint px-4 py-3">
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-alert" aria-hidden />
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold text-alert">This didn't load.</p>
        <p className="mt-0.5 break-words text-fg-dim">{text}</p>
      </div>
      {retry && (
        <button type="button" className="btn btn-quiet btn-sm" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}

/** Rows in the shape of the list that is coming. */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="divide-y divide-edge rounded-lg border border-edge bg-bg-raised" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-4">
          <div className="grid flex-1 gap-2">
            <div className="skeleton h-3.5 w-40" />
            <div className="skeleton h-3 w-64 max-w-full" />
          </div>
          <div className="skeleton h-5 w-16 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** A small section label inside a page. */
export function SectionLabel({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <h2 className="text-sm font-semibold">{children}</h2>
      {aside && <div className="text-xs text-fg-dim">{aside}</div>}
    </div>
  );
}
